import React from "react";
import { Button, Dropdown, Popover, Spin } from "antd";
import type { MenuProps } from "antd";
import { MoreOutlined, LinkOutlined } from "@ant-design/icons";
import "./index.css";

export type MemoCardModel = {
  id: string;
  title?: string;
  preview?: string;
  content?: string;
  createdAt?: string;
  createdBy?: string;
};

type Props = {
  memo: MemoCardModel;
  checked: boolean;
  onCheck: (id: string, checked: boolean) => void;

  onOpen: (memo: MemoCardModel) => void;
  onEdit: (memo: MemoCardModel) => void;
  onAddToGroup: (memo: MemoCardModel) => void;
  onDelete: (memo: MemoCardModel) => void;

  // ✅ NEW: open linked media modal
  onLink: (memo: MemoCardModel) => void;

  preview?: {
    title: string;
    description: string;
    linked_counts: Record<string, number>;
    group_count: number;
  } | null;

  previewLoading?: boolean;
  previewError?: string | null;

  onPreviewOpen?: (memoId: string) => void;
};

const MemoCard: React.FC<Props> = ({
  memo,
  checked,
  onCheck,
  onOpen,
  onEdit,
  onAddToGroup,
  onDelete,
  onLink,

  preview,
  previewLoading,
  previewError,
  onPreviewOpen,
}) => {
  const menuItems: MenuProps["items"] = [
    { key: "edit", label: "Edit memo" },
    { key: "group", label: "Add to memo group" },
    { key: "delete", label: <span className="memo-danger">Delete</span> },
  ];

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "edit") onEdit(memo);
    if (key === "group") onAddToGroup(memo);
    if (key === "delete") onDelete(memo);
  };

  const previewText = memo.preview || memo.content?.slice?.(0, 140) || "—";

  const renderPreview = () => {
    if (previewLoading)
      return (
        <div style={{ padding: 12 }}>
          <Spin size="small" />
        </div>
      );
    if (previewError) return <div style={{ padding: 12 }}>{previewError}</div>;
    if (!preview) return <div style={{ padding: 12 }}>—</div>;

    const counts = preview.linked_counts || {};
    const codes = counts.codes ?? counts.Codes ?? 0;
    const media = counts.media ?? counts.Media ?? 0;
    const highlights = counts.highlights ?? counts.Highlights ?? 0;
    const descriptors = counts.descriptors ?? counts.Descriptors ?? 0;

    return (
      <div style={{ width: 320, padding: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Memo preview</div>

        <div style={{ fontSize: 12, opacity: 0.75 }}>Title</div>
        <div style={{ marginBottom: 10 }}>{preview.title || "—"}</div>

        <div style={{ fontSize: 12, opacity: 0.75 }}>Description</div>
        <div style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
          {preview.description || "—"}
        </div>

        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
          Includes
        </div>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          <li>{codes} Codes</li>
          <li>{media} Media</li>
          <li>{highlights} Highlights</li>
          <li>{descriptors} Descriptors</li>
        </ul>

        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
          Groups: {preview.group_count ?? 0}
        </div>
      </div>
    );
  };

  return (
    <div className={`memo-item ${checked ? "memo-item-selected" : ""}`}>
      <div className="memo-item-top">
        <label className="memo-checkbox">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onCheck(memo.id, e.target.checked)}
          />
          <span className="memo-checkbox-ui" />
        </label>

        <div className="memo-item-title" title={memo.title}>
          {memo.title || "Untitled"}
        </div>

        <Dropdown
          menu={{ items: menuItems, onClick: onMenuClick }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button className="memo-kebab" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </div>
      <Popover
        content={renderPreview()}
        trigger="hover"
        placement="rightTop"
        mouseEnterDelay={0.25}
        onOpenChange={(open) => {
          if (open) onPreviewOpen?.(memo.id);
        }}
      >
        <div className="memo-item-body" onClick={() => onOpen(memo)}>
          <div className="memo-item-text">{previewText}</div>

          <div className="memo-item-footer">
            <div className="memo-meta">
              <div className="memo-by">By: {memo.createdBy || "—"}</div>
              <div className="memo-date2">{memo.createdAt || "—"}</div>
            </div>

            <Button
              className="memo-linkbtn"
              icon={<LinkOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onLink(memo); // ✅ open modal instead of onOpen
              }}
            />
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default MemoCard;
